import { useRemoteStrings, useAnalyticsClient } from '@acorns/web-utils'
import React, { useEffect } from 'react'
import { TestIDs } from 'test/utils/testIds'

import {
  ContextualDetailsContainer,
  ContextualDetailsContainerBackground,
  AppliedCodeCta,
  ContextualDetailsCondensedTitle,
  ContextualDetailsTitle,
  LoaderContainer,
  StyledCarrotArrowIcon,
  StyledCircleCheckIcon,
} from '../styled-components'
import { useReferralCode } from './useReferralCode'
import { SmallLoader } from '@acorns/grove'
import {
  RegistrationPromoCodeContainerViewedState,
  RegistrationPromoCodeEnterCodeCtaTappedState,
  trackRegistrationPromoCodeContainerViewedPayload,
  trackRegistrationPromoCodeEnterCodeCtaTappedPayload,
} from 'generated/trackingEvents'
import { translateEventProps } from 'utils/analytics'

interface Props {
  appliedPromoCode: string
  promoCodeChannel: string
  condensedTitle: string
  setIsDrawerOpen: React.Dispatch<React.SetStateAction<boolean>>
  hasPromoCodeQueryParam: boolean
  isPromoCodeVerificationLoading: boolean
}

export const AppliedCodeCard = ({
  appliedPromoCode,
  promoCodeChannel,
  condensedTitle,
  hasPromoCodeQueryParam,
  isPromoCodeVerificationLoading,
  setIsDrawerOpen,
}: Props) => {
  const { getRemoteString } = useRemoteStrings()
  const { hasReferralCode } = useReferralCode()
  const analytics = useAnalyticsClient()

  const isCtaEnabled = !hasReferralCode
  const changePromoCodeCtaTitle = getRemoteString({
    key: 'create_account.promo_code_card.edit.cta',
  })

  const handleDrawerOpen = () => {
    if (!hasReferralCode) {
      setIsDrawerOpen(true)
    }

    const [eventName, payLoad] = trackRegistrationPromoCodeEnterCodeCtaTappedPayload({
      promotion: appliedPromoCode,
      ctaTitle: changePromoCodeCtaTitle,
      state: RegistrationPromoCodeEnterCodeCtaTappedState.Edit_code,
    })

    analytics.track(eventName, translateEventProps(payLoad))
  }

  useEffect(() => {
    const [eventName, payLoad] = trackRegistrationPromoCodeContainerViewedPayload({
      promotion: appliedPromoCode,
      type: promoCodeChannel,
      state: RegistrationPromoCodeContainerViewedState.Edit_code,
    })

    analytics.track(eventName, translateEventProps(payLoad))
  }, [appliedPromoCode, promoCodeChannel])

  if (!hasReferralCode && appliedPromoCode?.length === 0) {
    return null
  }

  const displayedCode = isCtaEnabled
    ? appliedPromoCode
    : getRemoteString({
        key: 'create_account.referral.default.name',
      })

  if (hasPromoCodeQueryParam && isPromoCodeVerificationLoading) {
    return (
      <LoaderContainer>
        <SmallLoader testId={TestIDs.SPINNER} />
      </LoaderContainer>
    )
  }
  return (
    <ContextualDetailsContainer
      data-testid={TestIDs.PROMO_CODES.PROMO_CODE_APPLIED_CODE}
      isCtaEnabled={isCtaEnabled}
      onClick={handleDrawerOpen}
    >
      <ContextualDetailsTitle>
        <StyledCircleCheckIcon />
        {getRemoteString({
          key: 'create_account.promo_code_card.title.variable',
          variables: [displayedCode],
        })}
      </ContextualDetailsTitle>
      <ContextualDetailsCondensedTitle>
        {isCtaEnabled
          ? condensedTitle
          : getRemoteString({
              key: 'create_account.referral.default.condensed_title',
            })}
      </ContextualDetailsCondensedTitle>
      {isCtaEnabled && (
        <AppliedCodeCta testId="">
          {changePromoCodeCtaTitle}
          <StyledCarrotArrowIcon />
        </AppliedCodeCta>
      )}
      <ContextualDetailsContainerBackground />
    </ContextualDetailsContainer>
  )
}
